import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { otherInsuranceProviderSchema } from "~/schemas/SignUp/InsuranceProvider";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { Input } from "~/ui";
import { tw } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

export const OtherInsuranceProviderForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: InsuranceProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.otherProvider
    : primaryInsurance.otherProvider;

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "other_insurance", // see table below
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof otherInsuranceProviderSchema>>({
    mode: "onBlur",
    resolver: zodResolver(otherInsuranceProviderSchema),
    defaultValues: {
      otherProvider: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("otherProvider", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const onSubmit: SubmitHandler<
    z.infer<typeof otherInsuranceProviderSchema>
  > = (data) => {
    setPatientInsuranceField(
      "otherProvider",
      data.otherProvider,
      isSecondaryProvider,
    );
    trackComplete();
    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("otherProvider", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  const title = isSecondaryProvider
    ? "Please specify, who is your secondary insurance provider?"
    : "Please specify, who is your primary insurance provider?";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-20 self-center" />
        <div className="flex flex-col gap-3">
          <h1 className="font-display text-2xl">{title}</h1>
          <span className="text-lg">
            For patients that have an insurance plan not listed
          </span>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <Input
            placeholder="Insurance Provider*"
            id="otherProvider"
            compact
            {...register("otherProvider")}
          />
        </div>
        {errors.otherProvider && (
          <ErrorMessage message={errors.otherProvider.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
